import React, { useState, useEffect } from 'react';
import { XMarkIcon, MagnifyingGlassIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { Viewer, Worker } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import { toolbarPlugin } from '@react-pdf-viewer/toolbar';
import { searchPlugin } from '@react-pdf-viewer/search';

// Import CSS
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import '@react-pdf-viewer/toolbar/lib/styles/index.css';
import '@react-pdf-viewer/search/lib/styles/index.css';

// Get API base URL from environment or default
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Helper function to construct full URLs for direct fetch operations
const getFullApiUrl = (path) => {
  // If API_BASE_URL is relative (local dev), construct full URL with backend port
  if (API_BASE_URL.startsWith('/')) {
    // For local development, assume backend runs on port 8000
    return `${window.location.protocol}//${window.location.hostname}:8000${API_BASE_URL}${path}`;
  }
  // If API_BASE_URL is absolute (production), use it directly
  return `${API_BASE_URL}${path}`;
};

const PDFDocumentViewer = ({ documentId, isOpen, onClose, verbatimProposals = [], subtopicName, semanticGroupName }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [currentProposalIndex, setCurrentProposalIndex] = useState(0);
  
  // Comment state
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [authorName, setAuthorName] = useState('');
  const [authorEmail, setAuthorEmail] = useState('');
  const [commentLoading, setCommentLoading] = useState(false);
  const [commentError, setCommentError] = useState(null);
  const [autoSaveTimeout, setAutoSaveTimeout] = useState(null);

  // Create plugins
  const searchPluginInstance = searchPlugin({
    // Configure search to highlight complete matches
    onHighlightKeyword: (props) => {
      // After finding the text with first 5 words, expand highlight to next full stop
      setTimeout(() => {
        const highlightedElements = document.querySelectorAll('.rpv-search__highlight');
        if (highlightedElements.length > 0) {
          // Get the first highlighted element
          const firstHighlight = highlightedElements[0];
          
          // Try to find the text layer that contains this highlight
          let textLayer = firstHighlight.closest('.rpv-core__text-layer');
          if (!textLayer) {
            textLayer = firstHighlight.closest('[data-testid="core__text-layer"]');
          }
          if (!textLayer) {
            textLayer = firstHighlight.parentElement;
          }
          
          if (textLayer) {
            // Get all text content from the text layer
            const fullText = textLayer.textContent || textLayer.innerText;
            const highlightedText = firstHighlight.textContent || firstHighlight.innerText;
            
            // Find the position of our highlighted text in the full text
            const startIndex = fullText.toLowerCase().indexOf(highlightedText.toLowerCase());
            
            if (startIndex !== -1) {
              // Find the start of the sentence (look backwards for previous sentence end)
              let sentenceStart = startIndex;
              for (let i = startIndex - 1; i >= 0; i--) {
                if (/[.!?]/.test(fullText[i])) {
                  sentenceStart = i + 1;
                  break;
                }
                if (i === 0) {
                  sentenceStart = 0;
                }
              }
              
              // Find the end of the sentence (look forwards for next sentence end)
              let sentenceEnd = startIndex + highlightedText.length;
              for (let i = sentenceEnd; i < fullText.length; i++) {
                if (/[.!?]/.test(fullText[i])) {
                  sentenceEnd = i + 1;
                  break;
                }
              }
              
              // Extract the complete sentence
              const completeSentence = fullText.substring(sentenceStart, sentenceEnd).trim();
              
              // Only expand if we found a longer sentence
              if (completeSentence.length > highlightedText.length && completeSentence.length > 20) {
                // Clear current highlights and highlight the complete sentence
                clearHighlights();
                setTimeout(() => {
                  highlight(completeSentence);
                }, 100);
              }
            }
          }
          
          // Scroll the highlighted element to center of viewport
          setTimeout(() => {
            const newHighlightedElements = document.querySelectorAll('.rpv-search__highlight');
            if (newHighlightedElements.length > 0) {
              const elementToScroll = newHighlightedElements[0];
              
              // Get the element's position relative to the viewport
              const rect = elementToScroll.getBoundingClientRect();
              const viewportHeight = window.innerHeight;
              const elementCenter = rect.top + rect.height / 2;
              const viewportCenter = viewportHeight / 2;
              
              // Only scroll if the element is not already reasonably centered
              if (Math.abs(elementCenter - viewportCenter) > 100) {
                elementToScroll.scrollIntoView({
                  behavior: 'smooth',
                  block: 'center',
                  inline: 'nearest'
                });
              }
            }
          }, 200);
        }
      }, 300);
    }
  });
  const { highlight, clearHighlights } = searchPluginInstance;

  const defaultLayoutPluginInstance = defaultLayoutPlugin({
    sidebarTabs: () => [], // Disable all sidebar tabs for performance
  });

  // const toolbarPluginInstance = toolbarPlugin(); // Unused for now

  useEffect(() => {
    if (isOpen && documentId) {
      loadPDF();
      // loadComments(); // Commented out - comments feature not implemented yet
    }
  }, [isOpen, documentId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-highlight proposals when PDF loads
  useEffect(() => {
    if (verbatimProposals && verbatimProposals.length > 0 && pdfUrl) {
      // Delay to ensure PDF is fully loaded before highlighting
      const timer = setTimeout(() => {
        highlightCurrentProposal();
      }, 1500); // Increased delay for better performance
      
      return () => clearTimeout(timer);
    }
  }, [verbatimProposals, pdfUrl, currentProposalIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup auto-save timeout on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimeout) {
        clearTimeout(autoSaveTimeout);
      }
    };
  }, [autoSaveTimeout]);

  const loadPDF = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Generate PDF URL - use direct backend URL
      const url = getFullApiUrl(`/documents/${documentId}/pdf`);
      console.log('🔍 Loading PDF from URL:', url);
      
      // Set URL directly - let PDF.js handle the loading and 404 detection
      setPdfUrl(url);
      console.log('✅ PDF URL set successfully:', url);
      // Don't set loading to false here - let onDocumentLoad handle it
    } catch (err) {
      console.error('❌ Error loading PDF:', err);
      setError(`Unable to load document: ${err.message}`);
      setLoading(false);
    }
  };

  const highlightCurrentProposal = () => {
    if (verbatimProposals && verbatimProposals.length > 0 && currentProposalIndex < verbatimProposals.length) {
      const proposal = verbatimProposals[currentProposalIndex];
      const searchText = proposal.proposal_text || proposal.supporting_quote;
      
      if (searchText && searchText.length > 10) {
        // Clear previous highlights first
        clearHighlights();
        
        // Use first few words for more reliable matching
        const words = searchText.trim().split(/\s+/);
        const searchWords = words.slice(0, Math.min(5, words.length)).join(' ');
        
        // Try multiple search strategies
        setTimeout(() => {
          // Strategy 1: Search for first 5 words
          highlight(searchWords);
          
          // Strategy 2: If no results after 1 second, try first 3 words
          setTimeout(() => {
            const highlightedElements = document.querySelectorAll('.rpv-search__highlight');
            if (highlightedElements.length === 0 && words.length >= 3) {
              const shorterSearch = words.slice(0, 3).join(' ');
              highlight(shorterSearch);
            }
          }, 1000);
          
          // Strategy 3: If still no results, try key phrases
          setTimeout(() => {
            const highlightedElements = document.querySelectorAll('.rpv-search__highlight');
            if (highlightedElements.length === 0) {
              // Extract key phrases from the proposal text
              const keyPhrases = extractKeyPhrases(searchText);
              if (keyPhrases.length > 0) {
                highlight(keyPhrases[0]);
              }
            }
          }, 2000);
        }, 100);
      }
    }
  };

  const extractKeyPhrases = (text) => {
    // Extract meaningful phrases from the proposal text
    const phrases = [];
    
    // Look for "Proposal X:" patterns
    const proposalMatch = text.match(/Proposal\s+\d+[:\.]?\s*([^.]+)/i);
    if (proposalMatch) {
      phrases.push(proposalMatch[1].trim());
    }
    
    // Look for technical terms
    const technicalTerms = [
      'A-IoT', 'service type', 'paging message', 'device ID', 'access type',
      'RAN2', 'AS layer', 'service ID', 'command type', 'inventory'
    ];
    
    for (const term of technicalTerms) {
      if (text.toLowerCase().includes(term.toLowerCase())) {
        // Find a sentence containing this term
        const sentences = text.split(/[.!?]/);
        for (const sentence of sentences) {
          if (sentence.toLowerCase().includes(term.toLowerCase()) && sentence.trim().length > 10) {
            const words = sentence.trim().split(/\s+/);
            if (words.length >= 3) {
              phrases.push(words.slice(0, Math.min(5, words.length)).join(' '));
              break;
            }
          }
        }
      }
    }
    
    return phrases;
  };



  const navigateProposal = (direction) => {
    if (!verbatimProposals || verbatimProposals.length === 0) return;
    
    let newIndex;
    if (direction === 'next') {
      newIndex = (currentProposalIndex + 1) % verbatimProposals.length;
    } else {
      newIndex = currentProposalIndex === 0 ? verbatimProposals.length - 1 : currentProposalIndex - 1;
    }
    
    setCurrentProposalIndex(newIndex);
  };

  const jumpToProposal = (index) => {
    setCurrentProposalIndex(index);
  };

  // Comment functions
  const loadComments = async () => {
    try {
      const params = new URLSearchParams();
      if (subtopicName) params.append('subtopic_name', subtopicName);
      if (semanticGroupName) params.append('semantic_group_name', semanticGroupName);
      
      const url = getFullApiUrl(`/documents/${documentId}/comments${params.toString() ? '?' + params.toString() : ''}`);
      const response = await fetch(url);
      
      if (response.ok) {
        const commentsData = await response.json();
        setComments(commentsData);
      } else {
        console.error('Failed to load comments:', response.statusText);
      }
    } catch (err) {
      console.error('Error loading comments:', err);
    }
  };

  const saveComment = async (commentText, isUpdate = false, commentId = null) => {
    if (!commentText.trim()) return;
    
    setCommentLoading(true);
    setCommentError(null);
    
    try {
      const commentData = {
        document_id: documentId,
        subtopic_name: subtopicName,
        semantic_group_name: semanticGroupName,
        comment_text: commentText.trim(),
        author_name: authorName.trim() || null,
        author_email: authorEmail.trim() || null
      };

      let response;
      if (isUpdate && commentId) {
        // Update existing comment
        response = await fetch(getFullApiUrl(`/documents/${documentId}/comments/${commentId}`), {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            comment_text: commentText.trim(),
            author_name: authorName.trim() || null,
            author_email: authorEmail.trim() || null
          }),
        });
      } else {
        // Create new comment
        response = await fetch(getFullApiUrl(`/documents/${documentId}/comments`), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(commentData),
        });
      }

      if (response.ok) {
        const savedComment = await response.json();
        
        if (isUpdate) {
          // Update the comment in the list
          setComments(prev => prev.map(c => c.id === commentId ? savedComment : c));
        } else {
          // Add new comment to the list
          setComments(prev => [savedComment, ...prev]);
          setNewComment('');
        }
      } else {
        const errorData = await response.json();
        setCommentError(errorData.detail || 'Failed to save comment');
      }
    } catch (err) {
      console.error('Error saving comment:', err);
      setCommentError('Network error while saving comment');
    } finally {
      setCommentLoading(false);
    }
  };

  const handleCommentSubmit = () => {
    // Clear auto-save timeout to prevent double-saving
    if (autoSaveTimeout) {
      clearTimeout(autoSaveTimeout);
      setAutoSaveTimeout(null);
    }
    saveComment(newComment);
  };

  const handleAutoSave = (commentText) => {
    // Clear existing timeout
    if (autoSaveTimeout) {
      clearTimeout(autoSaveTimeout);
    }
    
    // Set new timeout for auto-save after 3 seconds of inactivity
    const timeout = setTimeout(() => {
      if (commentText.trim() && commentText.length > 10) {
        // Only auto-save if there's substantial content and it's different from what's saved
        saveComment(commentText);
      }
    }, 3000);
    
    setAutoSaveTimeout(timeout);
  };

  const deleteComment = async (commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;
    
    try {
      const response = await fetch(getFullApiUrl(`/documents/${documentId}/comments/${commentId}`), {
        method: 'DELETE',
      });

      if (response.ok) {
        setComments(prev => prev.filter(c => c.id !== commentId));
      } else {
        const errorData = await response.json();
        alert(errorData.detail || 'Failed to delete comment');
      }
    } catch (err) {
      console.error('Error deleting comment:', err);
      alert('Network error while deleting comment');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="flex h-full">
        {/* Background overlay */}
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

        {/* Main content area - removed margin-right to eliminate gap */}
        <div className="flex-1 flex flex-col bg-white transition-all duration-300 relative z-10">
          {/* Header */}
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4 border-b border-gray-200 relative z-10">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Document: {documentId}
                </h3>
                
                {/* Proposal navigation */}
                {verbatimProposals && verbatimProposals.length > 0 && (
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => navigateProposal('prev')}
                      className="p-1 text-gray-500 hover:text-gray-700 rounded"
                      disabled={verbatimProposals.length <= 1}
                    >
                      <ChevronLeftIcon className="h-4 w-4" />
                    </button>
                    <span className="text-sm text-gray-600">
                      Proposal {currentProposalIndex + 1} of {verbatimProposals.length}
                    </span>
                    <button
                      onClick={() => navigateProposal('next')}
                      className="p-1 text-gray-500 hover:text-gray-700 rounded"
                      disabled={verbatimProposals.length <= 1}
                    >
                      <ChevronRightIcon className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>

              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {sidebarCollapsed ? 'Show Info' : 'Hide Info'}
                </button>

                <button
                  onClick={onClose}
                  className="inline-flex items-center px-3 py-2 border border-red-300 shadow-sm text-sm leading-4 font-medium rounded-md text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                  title="Close Document Viewer"
                >
                  <XMarkIcon className="h-5 w-5 mr-1" />
                  Close
                </button>
              </div>
            </div>
          </div>

          {/* PDF Content - improved height and scrolling */}
          <div className="flex-1 bg-white overflow-hidden">
            {loading && !pdfUrl && (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-2 text-gray-600">Loading document...</span>
              </div>
            )}

            {error && (
              <div className={`border rounded-md p-8 m-8 text-center ${
                error === 'NOT_FOUND' 
                  ? 'bg-gray-50 border-gray-200' 
                  : 'bg-red-50 border-red-200'
              }`}>
                {error === 'NOT_FOUND' ? (
                  <>
                    <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-gray-100 mb-4">
                      <svg className="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Document Not Found</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      The PDF for document <strong>{documentId}</strong> is not available.<br/>
                      This document may not have been downloaded from the 3GPP server.
                    </p>
                    <button
                      onClick={onClose}
                      className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                    >
                      Close
                    </button>
                  </>
                ) : (
                  <>
                    <h3 className="text-sm font-medium text-red-800 mb-2">Error Loading Document</h3>
                    <p className="text-sm text-red-700 mb-4">{error}</p>
                    <div className="space-x-3">
                      <button
                        onClick={() => {
                          setError(null);
                          setLoading(true);
                          loadPDF();
                        }}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        Retry Loading
                      </button>
                      <button
                        onClick={onClose}
                        className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                      >
                        Close
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            {pdfUrl && !error && (
              <div className="h-full w-full relative">
                {loading && (
                  <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center z-10">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                      <p className="mt-2 text-sm text-gray-600">Loading PDF...</p>
                    </div>
                  </div>
                )}
                <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                  <Viewer
                    fileUrl={pdfUrl}
                    plugins={[defaultLayoutPluginInstance, searchPluginInstance]}
                    defaultScale={2.1}
                    scrollMode="vertical"
                    onDocumentLoad={(e) => {
                      console.log('✅ PDF loaded successfully:', e);
                      setLoading(false);
                    }}
                    onDocumentLoadError={(error) => {
                      console.error('❌ PDF load error:', error);
                      // Check if it's a 404 error
                      const errorMsg = error.message || 'Unknown error';
                      if (errorMsg.includes('404') || errorMsg.includes('Not Found')) {
                        setError('NOT_FOUND');
                      } else {
                        setError(`PDF is not available to render: ${errorMsg}`);
                      }
                      setLoading(false);
                    }}
                  />
                </Worker>
              </div>
            )}
          </div>
        </div>

        {/* Right Sidebar - positioned absolutely to overlay, starting below header */}
        {!sidebarCollapsed && (
          <div className="absolute right-0 top-20 bottom-0 w-96 bg-gray-50 border-l border-gray-200 flex flex-col z-20">
            {/* Sidebar Header */}
            <div className="p-4 border-b border-gray-200 bg-white">
              <h4 className="text-xl font-semibold text-gray-900 mb-3">Document Context</h4>
              
              {/* Only show Subtopic/Semantic Group info if there are verbatim proposals */}
              {verbatimProposals && verbatimProposals.length > 0 && (
                <>
                  {/* Subtopic Info */}
                  {subtopicName && (
                    <div className="mb-3">
                      <label className="text-sm font-medium text-gray-500 uppercase tracking-wide">Subtopic</label>
                      <p className="text-base text-gray-900 mt-1">{subtopicName}</p>
                    </div>
                  )}

                  {/* Semantic Group Info */}
                  {semanticGroupName && (
                    <div className="mb-3">
                      <label className="text-sm font-medium text-gray-500 uppercase tracking-wide">Semantic Cluster</label>
                      <p className="text-base text-gray-900 mt-1">{semanticGroupName}</p>
                    </div>
                  )}

                  {/* Proposal Count */}
                  <div>
                    <label className="text-sm font-medium text-gray-500 uppercase tracking-wide">Highlighted Proposals</label>
                    <p className="text-base text-gray-900 mt-1">{verbatimProposals.length} proposal{verbatimProposals.length !== 1 ? 's' : ''}</p>
                  </div>
                </>
              )}
            </div>

            {/* Proposals List */}
            <div className="flex-1 overflow-y-auto p-4 space-y-6">
              {verbatimProposals && verbatimProposals.length > 0 ? (
                <div className="space-y-3">
                  <h5 className="text-base font-medium text-gray-900 mb-3">
                    Proposals that relate to {semanticGroupName}
                  </h5>
                  {(() => {
                    // Deduplicate proposals by proposal_text
                    const seen = new Set();
                    const uniqueProposals = verbatimProposals.filter(proposal => {
                      const key = proposal.proposal_text || proposal.supporting_quote || '';
                      if (seen.has(key)) {
                        return false;
                      }
                      seen.add(key);
                      return true;
                    });
                    
                    return uniqueProposals.map((proposal, index) => (
                      <div
                        key={index}
                        className={`p-3 rounded-lg border cursor-pointer transition-all ${
                          index === currentProposalIndex
                            ? 'bg-yellow-50 border-yellow-300 shadow-sm'
                            : 'bg-white border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => jumpToProposal(index)}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <span className="text-sm font-medium text-gray-500">
                            Proposal {index + 1}
                          </span>
                          {index === currentProposalIndex && (
                            <span className="text-sm bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                              Current
                            </span>
                          )}
                        </div>
                        <p className="text-base text-gray-700 leading-relaxed">
                          {proposal.proposal_text || proposal.supporting_quote || 'No proposal text available'}
                        </p>
                      </div>
                    ));
                  })()}
                </div>
              ) : (
                <div className="py-4 px-3">
                  {subtopicName && !subtopicName.startsWith('Document:') ? (
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <h5 className="text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">Document Summary</h5>
                      <p className="text-base text-gray-700 leading-relaxed whitespace-pre-line">
                        {subtopicName}
                      </p>
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <MagnifyingGlassIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-base text-gray-500">No highlighted proposals for this document</p>
                    </div>
                  )}
                </div>
              )}

            </div>

            {/* Sidebar Footer */}
            <div className="p-4 border-t border-gray-200 bg-white">
              <p className="text-sm text-gray-500">
                Use the search function in the PDF viewer to find specific text, or navigate through highlighted proposals using the controls above.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PDFDocumentViewer; 